import { useRef } from "react";
import { createPortal } from "react-dom";
import SortByIcon from "../../../../../assets/svg/sort-by-icon";
import RightArrowIcon from "../../../../../assets/svg/right-arrow-icon";
import { useFilterPortalPosition } from "../../../../Crm/components/KanbanGrid/components/tableFilters/useFilterPortalPosition";

export const SortableColumnHeader = ({
  label,
  sortField,
  activeSortKey,
  activeSortOrder,
  onSort,
  onTogglePopper,
  isPopperOpen,
}: {
  label: string;
  sortField: string;
  activeSortKey?: string | null;
  activeSortOrder?: "asc" | "desc" | null;
  onSort: (key: string, order: "asc" | "desc" | undefined) => void;
  onTogglePopper: (key: string) => void;
  isPopperOpen: boolean;
}) => {
  const isActive = activeSortKey === sortField;
  const triggerRef = useRef<HTMLDivElement>(null);
  const pos = useFilterPortalPosition(triggerRef);

  const handleSortTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTogglePopper(sortField);
  };

  const handleSortAsc = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSort(sortField, "asc");
    onTogglePopper(sortField);
  };

  const handleSortDesc = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSort(sortField, "desc");
    onTogglePopper(sortField);
  };

  const handleSortReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSort(sortField, undefined);
    onTogglePopper(sortField);
  };

  return (
    <div className="relative">
      <div
        ref={triggerRef}
        className="text-left px-3 py-2 flex items-center gap-1 cursor-pointer select-none"
        data-sort-trigger
        onClick={handleSortTriggerClick}
      >
        <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk whitespace-nowrap">
          {label}
        </span>
        <SortByIcon
          className={`w-4 h-4 shrink-0 text-verba-blue dark:text-verba-blue-dk transition-transform ${
            isActive && activeSortOrder === "desc" ? "rotate-180" : ""
          }`}
        />
      </div>
      {isPopperOpen &&
        pos &&
        createPortal(
          <div
            data-sort-popper
            className="fixed z-[20001] rounded-[8px] border border-border-gray dark:border-border-gray-dk bg-popper-bg/80 dark:bg-popper-bg-dk/80 backdrop-blur-[20px] shadow-[10px_10px_60px_0px_rgba(0,0,0,0.2)] p-[12px] flex flex-col gap-[16px]"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="flex flex-col min-w-[130px] gap-1">
              <button
                className={`flex items-center gap-[4px] px-[8px] py-[8px] rounded-[8px] cursor-pointer w-full ${
                  isActive && activeSortOrder === "asc"
                    ? "bg-hover-effect dark:bg-hover-effect-dk"
                    : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                }`}
                onClick={handleSortAsc}
              >
                <RightArrowIcon className="w-[18px] h-[18px] text-heading-text dark:text-heading-text-dk shrink-0 rotate-270" />
                <span className="text-md text-heading-text dark:text-heading-text-dk whitespace-nowrap">
                  Sort ascending
                </span>
              </button>
              <button
                className={`flex items-center gap-[4px] px-[8px] py-[8px] rounded-[8px] cursor-pointer w-full ${
                  isActive && activeSortOrder === "desc"
                    ? "bg-hover-effect dark:bg-hover-effect-dk"
                    : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                }`}
                onClick={handleSortDesc}
              >
                <RightArrowIcon className="w-[18px] h-[18px] text-heading-text dark:text-heading-text-dk shrink-0 rotate-90" />
                <span className="text-md text-heading-text dark:text-heading-text-dk whitespace-nowrap">
                  Sort descending
                </span>
              </button>
            </div>
            <button
              className="text-xs font-semibold text-verba-blue dark:text-verba-blue-dk cursor-pointer text-left px-[8px] whitespace-nowrap"
              onClick={handleSortReset}
            >
              Reset
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export const FilterableColumnHeader = ({
  label,
  filterKey,
  activeFilter,
  onToggleFilter,
  children,
}: {
  label: string;
  filterKey: string;
  activeFilter: string | null;
  onToggleFilter: (key: string) => void;
  children: (
    triggerRef: React.RefObject<HTMLDivElement | null>
  ) => React.ReactNode;
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleFilterTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFilter(filterKey);
  };

  return (
    <div className="relative">
      <div
        ref={triggerRef}
        className="text-left px-3 py-2 flex items-center gap-1 cursor-pointer"
        data-table-filter-trigger
        onClick={handleFilterTriggerClick}
      >
        <span className="text-md font-medium text-gray-375 dark:text-gray-375-dk whitespace-nowrap">
          {label}
        </span>
        <SortByIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk shrink-0" />
      </div>
      {activeFilter === filterKey && children(triggerRef)}
    </div>
  );
};
